/* Bluewud SKU search bridge.
 *
 * Shopify's storefront search doesn't match variant SKUs: the predictive API
 * only searches them when resources[options][fields] includes variants.sku,
 * and the full /search page ignores options[fields] entirely — so a SKU query
 * submitted with Enter lands on a page of fuzzy, unrelated matches.
 *
 * Three fixes, one file:
 *  1. FLYDOWN — empire.js fetches the predictive-search section with no
 *     fields list; rewrite that request to Shopify's default fields plus
 *     variants.sku so the dropdown matches SKUs.
 *  2. SUBMIT — on Enter with a SKU-shaped query, ask the predictive API for
 *     SKU-field matches first: exactly one product opens its page directly,
 *     anything else falls through to the normal search submit.
 *  3. RESULTS PAGE — on /search with a SKU-shaped q, fetch SKU-field matches
 *     and show them in a strip above the unreliable fuzzy results.
 */
(function () {
  'use strict';

  if (window.__bwSkuSearch) return;
  window.__bwSkuSearch = true;

  var SKU_FIELDS = 'variants.sku';
  /* Shopify's documented predictive defaults + variants.sku — a fields list
     REPLACES the defaults, so they must be repeated here. */
  var FLYDOWN_FIELDS = 'title,body,vendor,product_type,tag,author,variants.title,variants.sku';
  /* Measured ~180ms on a warm connection; the headroom is for slow mobile.
     On timeout the lookup resolves empty and the page simply renders as
     stock Shopify — the strip and the promoted card just don't appear. */
  var LOOKUP_TIMEOUT_MS = 5000;
  var MAX_STRIP_ITEMS = 6;
  /* Carries the shopper's original term through a variants.sku: redirect so
     the results page can show "SB-AA" rather than "variants.sku:SB-AA*". */
  var SKU_TERM_PARAM = 'bwsku';

  var nativeFetch = window.fetch;

  function routes() {
    return (window.Theme && window.Theme.routes) || {};
  }

  function predictiveUrl() {
    return routes().predictive_search_url || '/search/suggest';
  }

  function searchPath() {
    return (routes().search_url || '/search').replace(/\/+$/, '');
  }

  /* SKU-shaped: one token, either hyphenated (ST-RDO-STO) or letters+digits.
     Plain words and multi-word queries skip the SKU path entirely.

     A hyphenated non-SKU ("coffee-table") passes this test on purpose: the
     lookup below simply returns nothing and the query falls through to normal
     search. Being generous here costs one predictive call; being strict here
     costs an internal user a dead end. */
  function isSkuLike(q) {
    if (!q || q.length < 3 || q.length > 40 || /\s/.test(q)) return false;
    if (q.indexOf('-') > 0) return true;
    return /\d/.test(q) && /[a-z]/i.test(q);
  }

  /* Published so bluewud-search-intent.js can stand down and let this file own
     SKU submits. Both listen on document in the capture phase, so whichever
     script registered first would otherwise win by load order alone -- which
     is exactly how SKU submits ended up being routed by the intent layer into
     a fuzzy keyword search that returns nothing. */
  window.__bwIsSkuQuery = isSkuLike;

  /* ---- Show the shopper's term, not the query syntax -----------------
     A variants.sku: redirect leaves the raw Shopify query in the H1, the
     SKU strip heading and the document title -- "Search results for
     variants.sku:tu-aro*" is machine-speak. bwsku carries the term the
     shopper actually typed, so swap it back in. */
  function humaniseSkuHeadings() {
    try {
      var params = new URLSearchParams(location.search);
      var friendly = params.get(SKU_TERM_PARAM);
      var raw = params.get('q');
      if (!friendly || !raw || raw.indexOf('variants.sku:') !== 0) return;

      var bare = raw.replace(/^variants\.sku:/, '').replace(/\*$/, '');
      var needles = [raw, 'variants.sku:' + bare, bare];

      if (document.title.indexOf('variants.sku') !== -1) {
        document.title = 'Search: ' + friendly + ' — Bluewud';
      }

      var nodes = document.querySelectorAll('h1, h2, h3, .productgrid--masthead, .bw-sku-hits__title');
      nodes.forEach(function (node) {
        node.childNodes.forEach(function (child) {
          if (child.nodeType !== 3) return;
          var txt = child.nodeValue;
          needles.forEach(function (n) {
            if (n && txt.indexOf(n) !== -1) txt = txt.split(n).join(friendly);
          });
          if (txt !== child.nodeValue) child.nodeValue = txt;
        });
      });
    } catch (err) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', humaniseSkuHeadings);
  } else {
    humaniseSkuHeadings();
  }

  /* ---- 1. Teach the native flydown to match SKUs --------------------- */
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var hasFields = url.indexOf('resources[options][fields]') !== -1 ||
        url.indexOf('resources%5Boptions%5D%5Bfields%5D') !== -1;
      if (url.indexOf('section_id=predictive-search') !== -1 && !hasFields) {
        var patched = url + '&resources[options][fields]=' + FLYDOWN_FIELDS;
        input = typeof input === 'string' ? patched : new Request(patched, input);
      }
    } catch (err) {
      /* leave the request untouched */
    }
    return nativeFetch.call(this, input, init);
  };

  /* Query the predictive API against variant SKUs only; calls back with a
     (possibly empty) product array — never throws, times out to empty. */
  function skuLookup(q, done) {
    var controller = 'AbortController' in window ? new AbortController() : null;
    var timer = controller && setTimeout(function () { controller.abort(); }, LOOKUP_TIMEOUT_MS);
    var url = predictiveUrl() + '.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=' + MAX_STRIP_ITEMS +
      '&resources[options][fields]=' + SKU_FIELDS;

    nativeFetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (timer) clearTimeout(timer);
        var products = data && data.resources && data.resources.results &&
          data.resources.results.products;
        done(products || []);
      })
      .catch(function () {
        if (timer) clearTimeout(timer);
        done([]);
      });
  }

  /* ---- 2. Enter/submit: exact SKU goes straight to the product ------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches) return;
    /* Was scoped to form[data-live-search-form], which is ONLY the header
       flydown form. Every other search box on the site -- the search landing
       page's field included -- bypassed the SKU path entirely and fell
       through to a keyword search that cannot match SKUs. Match any form that
       posts to /search and carries a q field. */
    var isSearchForm = (form.getAttribute('action') || '').indexOf('/search') !== -1 ||
      form.hasAttribute('data-live-search-form');
    if (!isSearchForm || !form.querySelector('input[name="q"]')) return;
    if (form.dataset.bwSkuPass === '1') { delete form.dataset.bwSkuPass; return; }
    /* "View all results" in the flydown is an explicit ask for the results
       page — don't hijack it. */
    if (e.submitter && e.submitter.classList &&
        e.submitter.classList.contains('search-flydown__continue')) return;

    var input = form.querySelector('input[name="q"]');
    var q = input ? input.value.trim() : '';
    if (!isSkuLike(q)) return;

    e.preventDefault();
    e.stopPropagation();

    skuLookup(q, function (products) {
      /* Exactly one variant matched: open the product. This is the path the
         internal team uses dozens of times a day. */
      if (products.length === 1 && products[0].url) {
        window.location.assign(products[0].url);
        return;
      }

      /* Two or more: send them to a results page SCOPED TO SKUs. Resubmitting
         the raw term ran Shopify's keyword search, which does not index SKUs
         at all -- that is why pressing Enter said "nothing found" even though
         the dropdown had just shown the product. Verified against the live
         store: /search?q=variants.sku:SB-AA-WB returns 1 product,
         variants.sku:TU-ARO* returns 5, and it is case-insensitive. */
      if (products.length > 1) {
        var scoped = searchPath() + '?q=' + encodeURIComponent('variants.sku:' + q + '*') +
          '&type=product&' + SKU_TERM_PARAM + '=' + encodeURIComponent(q);
        window.location.assign(scoped);
        return;
      }

      /* Nothing matched a SKU. Fall through so the normal search path -- and
         the intent layer's budget/category routing -- gets its turn. */
      form.dataset.bwSkuPass = '1';
      if (form.requestSubmit) form.requestSubmit();
      else form.submit();
    });
  }, true);

  /* ---- 3. /search results page: SKU-match strip ---------------------- */
  function formatPrice(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', maximumFractionDigits: 0
      }).format(n);
    } catch (err) {
      return 'Rs. ' + Math.round(n);
    }
  }

  function buildStripItem(product) {
    var item = document.createElement('a');
    item.className = 'bw-sku-hits__item';
    item.href = product.url;

    var imageUrl = (product.featured_image && product.featured_image.url) || product.image || '';
    if (imageUrl) {
      var img = document.createElement('img');
      img.className = 'bw-sku-hits__img';
      img.src = imageUrl + (imageUrl.indexOf('?') === -1 ? '?' : '&') + 'width=120';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 56;
      img.height = 56;
      item.appendChild(img);
    }

    var text = document.createElement('span');
    text.className = 'bw-sku-hits__text';

    var name = document.createElement('span');
    name.className = 'bw-sku-hits__name';
    name.textContent = product.title;
    text.appendChild(name);

    var price = formatPrice(product.price);
    if (price) {
      var priceEl = document.createElement('span');
      priceEl.className = 'bw-sku-hits__price';
      priceEl.textContent = price;
      text.appendChild(priceEl);
    }

    item.appendChild(text);
    return item;
  }

  function injectStripStyles() {
    var style = document.createElement('style');
    style.textContent =
      '.bw-sku-hits{margin:14px 0 4px;padding:14px 16px 16px;border:1px solid #cfe3ea;border-radius:12px;background:#f4fafc}' +
      '.bw-sku-hits__title{margin:0 0 10px;font-size:15px;font-weight:700;color:#046e82}' +
      '.bw-sku-hits__items{display:flex;flex-wrap:wrap;gap:10px}' +
      '.bw-sku-hits__item{display:flex;align-items:center;gap:12px;padding:10px 14px;background:#fff;border:1px solid #e3e3e0;border-radius:10px;text-decoration:none;color:inherit;min-width:0;max-width:100%;box-shadow:0 2px 8px rgba(0,0,0,.05)}' +
      '.bw-sku-hits__item:hover{box-shadow:0 6px 14px rgba(0,0,0,.1)}' +
      '.bw-sku-hits__img{width:56px;height:56px;object-fit:contain;border-radius:8px;background:#f6f6f4;flex:0 0 auto}' +
      '.bw-sku-hits__text{min-width:0}' +
      '.bw-sku-hits__name{display:block;font-weight:600;font-size:14px;line-height:1.3;color:#1d1d1d}' +
      '.bw-sku-hits__price{display:block;margin-top:2px;font-size:13px;color:#555}' +
      /* outline (not border) so highlighting the promoted card costs no layout */
      '.bw-sku-card{outline:2px solid #046e82;outline-offset:2px;border-radius:8px}';
    document.head.appendChild(style);
  }

  function handleFromUrl(url) {
    if (!url) return '';
    var path = String(url).split('?')[0];
    var i = path.indexOf('/products/');
    return i === -1 ? '' : path.slice(i + 10).replace(/\/+$/, '');
  }

  function findCard(root, handle) {
    var link = root.querySelector('.productgrid--item a[href*="/products/' + handle + '"]');
    return link ? link.closest('.productgrid--item') : null;
  }

  /* Put the SKU-matched product at the FRONT of the real card grid.
     Shopify's fuzzy results usually already contain it, just buried (the
     Radisso sat at position 14 of 26) — so a card that's already present is
     MOVED, never cloned, and nothing can duplicate. Only a product the fuzzy
     search missed entirely is fetched, via the field-scoped `variants.sku:`
     query, which Shopify renders natively (the Section Rendering API drops the
     search context, so it has to be the full page).
     .productgrid--items is a plain flex row of identical cards, so inserting
     a real .productgrid--item here is exactly like a native one. */
  function promoteCards(q, products) {
    var grid = document.querySelector('.productgrid--items');
    if (!grid) return;

    var wanted = products.map(function (p) { return handleFromUrl(p.url); }).filter(Boolean);
    if (!wanted.length) return;

    var missing = [];
    /* Reverse: each prepend pushes the previous one right, so the first
       wanted handle ends up leftmost. */
    wanted.slice().reverse().forEach(function (handle) {
      var card = findCard(grid, handle);
      if (card) {
        card.classList.add('bw-sku-card');
        grid.prepend(card);
      } else {
        missing.push(handle);
      }
    });

    if (!missing.length) return;

    nativeFetch(searchPath() + '?q=' + encodeURIComponent('variants.sku:' + q))
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        missing.slice().reverse().forEach(function (handle) {
          if (findCard(grid, handle)) return; /* already there */
          var found = findCard(doc, handle);
          if (!found) return;
          var clone = found.cloneNode(true);
          clone.classList.add('bw-sku-card');
          grid.prepend(clone); /* bluewud-cards.js's observer binds swatches */
        });
      })
      .catch(function () { /* the strip still stands on its own */ });
  }

  function initResultsStrip() {
    if (window.location.pathname.replace(/\/+$/, '') !== searchPath()) return;
    if (document.querySelector('[data-bw-sku-strip]')) return;

    var params = new URLSearchParams(window.location.search);
    var q = (params.get('q') || '').trim();

    /* If the query is already variants.sku: scoped then the WHOLE results
       page is SKU matches -- the submit handler sent them here deliberately.
       A strip above it would repeat the same products and print the raw query
       syntax back at the shopper. The strip only earns its place when Shopify
       ran an ordinary fuzzy search and buried (or missed) the SKU. */
    if (q.indexOf('variants.sku:') === 0) return;

    if (!isSkuLike(q)) return;

    /* The panel MUST go INSIDE the masthead, never as a sibling of it.
       .productgrid--wrapper is a named-area CSS grid ("masthead masthead" /
       "sidebar productgrid--items"); any extra in-flow child gets auto-placed
       into a named cell and shunts the sidebar and the whole card grid into
       the wrong columns. The masthead is a plain block that already spans
       both columns, so nesting inside it disturbs nothing. */
    var masthead = document.querySelector('.productgrid--masthead');
    if (!masthead) return;

    skuLookup(q, function (products) {
      if (!products.length) return;

      injectStripStyles();

      var panel = document.createElement('section');
      panel.className = 'bw-sku-hits';
      panel.setAttribute('data-bw-sku-strip', '');
      panel.setAttribute('aria-label', 'Products matching SKU ' + q);

      var title = document.createElement('h2');
      title.className = 'bw-sku-hits__title';
      title.textContent = 'SKU match for “' + q + '”';
      panel.appendChild(title);

      var items = document.createElement('div');
      items.className = 'bw-sku-hits__items';
      products.slice(0, MAX_STRIP_ITEMS).forEach(function (product) {
        items.appendChild(buildStripItem(product));
      });
      panel.appendChild(items);

      masthead.appendChild(panel);
      promoteCards(q, products);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initResultsStrip);
  } else {
    initResultsStrip();
  }
})();
